import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import FormField from '../../../components/Auth/FormField';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AdminForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email address is required');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSubmitted(true);
    } catch (error) {
      setError('Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const keyIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
  );

  if (submitted) {
    return (
      <AuthLayout
        title="Check Your Email"
        subtitle="We've sent password reset instructions to your admin email address"
        icon={
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
        }
        iconBgColor="bg-jet-green-100"
        iconColor="text-jet-green-600"
      >
        <div className="text-center space-y-6">
          <div className="bg-jet-green-50 border border-jet-green-200 rounded-md p-4">
            <p className="text-sm text-jet-green-800">
              If an admin account with email <strong>{email}</strong> exists, 
              you will receive password reset instructions within the next few minutes.
            </p>
          </div>

          <div className="space-y-4">
            <LoadingButton
              onClick={() => navigate('/admin/auth/login')}
              variant="primary"
              className="bg-jet-red-600 hover:bg-jet-red-700 text-white"
            >
              Back to Admin Login
            </LoadingButton>

            <button
              onClick={() => {
                setSubmitted(false);
                setEmail('');
                setError('');
              }}
              className="w-full py-3 px-4 border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium rounded-md transition-colors"
            >
              Try Different Email
            </button>
          </div>

          <div className="text-sm text-jet-gray-600">
            Didn't receive the email? Check your spam folder or contact IT support.
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset Admin Password"
      subtitle="Enter your admin email address and we'll send you instructions to reset your password"
      icon={keyIcon}
      iconBgColor="bg-jet-orange-100"
      iconColor="text-jet-orange-600"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Security Notice */}
        <div className="bg-jet-orange-50 border border-jet-orange-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-orange-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-orange-900 mb-1">Admin Security Notice</h4>
              <p className="text-sm text-jet-orange-800">
                Admin password reset requests are logged and monitored for security purposes.
              </p>
            </div>
          </div>
        </div>

        <FormField
          label="Admin Email Address"
          name="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError('');
          }}
          placeholder="Enter your admin email address"
          required
          error={error}
        />

        <LoadingButton
          type="submit"
          loading={loading}
          loadingText="Sending Reset Link..."
          variant="primary"
          className="bg-jet-red-600 hover:bg-jet-red-700 text-white"
        >
          Send Reset Instructions
        </LoadingButton>

        {/* Additional Links */}
        <div className="text-center space-y-3">
          <button
            type="button"
            onClick={() => navigate('/admin/auth/login')}
            className="text-jet-red-600 hover:text-jet-red-700 text-sm transition-colors"
          >
            Back to Admin Login
          </button>
          
          <div className="text-sm text-jet-gray-600">
            Need admin access?{' '}
            <button
              type="button"
              onClick={() => navigate('/admin/auth/register')}
              className="text-jet-red-600 hover:text-jet-red-700 font-medium"
            >
              Request Account
            </button>
          </div>
        </div>

        {/* Contact Support */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <p className="text-sm text-jet-gray-600 mb-2">
            Having trouble accessing your admin account?
          </p>
          <button
            type="button"
            onClick={() => alert('Contact IT Support: admin-support@jetscanner360.com')}
            className="text-jet-blue-600 hover:text-jet-blue-700 text-sm font-medium transition-colors"
          >
            Contact IT Support
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AdminForgotPasswordPage;